'use client'

// The things he asked for, standing in the town once they are granted.
//
// He asks at the shrine (sim world/Asks.js) and every ask points at a real
// moment in his day. Sam answers; a granted ask is told to him at the
// shrine, and this is the other half of it: the thing is there. All of
// them live in wants.glb, authored in Blender at world coordinates (so it
// renders at IDENTITY), one root node per want named ask_<id> after the
// ask that wanted it. A want is drawn only while its ask says granted, so
// nothing turns up in the town that he was never told about, and a want
// answered no later goes again.
//
// Inside each want: meshes, a stand_<key> empty where he stands to use it
// (the sim's business, lib/world/wants.js), block_<key>_<n> boxes that are
// collision only (ObstacleProvider sends them, they are never drawn), and
// glow_* meshes, the bulbs and lamps, drawn unlit so they read as light. And lit_* meshes, a vending machine's front:
// lit as anything is, and glowing a little with their own picture, the way a machine lit from inside reads in the sun
// and in the dark (28 Oct; Sam: "it'll almost always be in the shade and it looks naff").

import { useEffect, useMemo, useState } from 'react'
import * as THREE from 'three/webgpu'
import { useModel } from '../hooks/useModel'
import { useSceneNodeMaterials } from '../hooks/useSceneNodeMaterials'
import { ditherMaterial } from '../effects/ditherRegistry'

const MODEL = '/models/wants.glb'
// how much of a lit_ mesh's own picture it gives off as light
const LIT_GLOW = 0.42
const POLL_MS = 120_000

/** The ids of the asks that have been granted, from the public asks door. */
function useGranted() {
  const [granted, setGranted] = useState(null)
  useEffect(() => {
    let alive = true
    const pull = async () => {
      try {
        const res = await fetch('/api/asks?limit=200', { cache: 'no-store' })
        const j = await res.json()
        if (alive && Array.isArray(j?.list)) setGranted(new Set(j.list.filter((a) => a.status === 'granted').map((a) => a.id)))
      } catch { /* keep what we had */ }
    }
    pull()
    const t = setInterval(pull, POLL_MS)
    return () => { alive = false; clearInterval(t) }
  }, [])
  return granted
}

export function Wants() {
  const { scene } = useModel(MODEL)
  useSceneNodeMaterials(scene, { roughness: 0.95 })
  const granted = useGranted()

  // The roots, and the one-time dressing of what is inside them
  const roots = useMemo(() => {
    const out = []
    if (!scene) return out
    const bulbs = new Map()
    const lit = new Map()
    scene.traverse((o) => {
      // a want starts hidden: nothing shows before we know it was granted
      if (/^ask_[a-z0-9]+$/.test(o.name || '')) { o.visible = false; out.push(o) }
      if (!o.isMesh) return
      if (/^block_/.test(o.name)) { o.visible = false; return }
      if (/^glow_/.test(o.name)) {
        const src = Array.isArray(o.material) ? o.material[0] : o.material
        if (!bulbs.has(src)) {
          const m = new THREE.MeshBasicNodeMaterial({ color: '#ffd98a', map: src?.map || null })
          bulbs.set(src, m)
        }
        o.material = bulbs.get(src)
        o.castShadow = false
      }
      if (/^lit_/.test(o.name)) {
        const src = Array.isArray(o.material) ? o.material[0] : o.material
        if (src?.map && !lit.has(src)) {
          const m = src.clone()
          m.emissive = new THREE.Color('#ffffff')
          m.emissiveMap = src.map
          m.emissiveIntensity = LIT_GLOW
          lit.set(src, m)
        }
        if (lit.has(src)) o.material = lit.get(src)
      }
    })
    return out
  }, [scene])

  // Glass the keys can be seen through: blended, and it must not hide what
  // is behind it from the depth buffer
  useEffect(() => {
    if (!scene) return
    scene.traverse((o) => {
      if (!o.isMesh) return
      const mats = Array.isArray(o.material) ? o.material : [o.material]
      for (const m of mats) {
        if (m?.transparent) { m.depthWrite = false; ditherMaterial(m) }
      }
    })
  }, [scene])

  useEffect(() => {
    for (const r of roots) r.visible = !!granted && granted.has(r.name)
  }, [roots, granted])

  return <primitive object={scene} />
}

export default Wants
